import { setPickerFilters, setShowSuccess, toggleFormHelp } from 'actions/ui';
import MetricPicker from 'components/admin/forms/metricPicker';
import { ModalPicker } from 'components/user/modalPicker';
import { ArrayHelpers, Forms, Help, Modal, SaveButton, StringHelpers, SystemText, TitleBar } from 'e4shared';
import { hasModule } from 'helpers/profile';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MetricTarget from './components/metricTarget';
import Files from 'components/admin/forms/files';
import { setDashboardCollection } from 'actions/dashboard';
import { apiGet } from 'services';
import { add, update, load } from 'actions/admin';
import { competenciesByProfiles } from 'actions/profile';
import { addWeeks } from 'helpers/dates';
import { getMetricFromPickerDetails } from 'helpers/metrics';
import { lmsSystemRefs, lmsSystems } from 'helpers/lms';
import posthog from 'posthog-js';
import { translateCompetency } from 'helpers/translations';
import { Copy } from 'e4shared';
import { uploadType } from 'helpers/azure';

const fallbackValues = () => ({
    pathway_Id: null,
    task: '',
    name: '',
    description: '',
    profile_Ids: [],
    competencies: [],
    due: addWeeks(new Date(), 1),
    due_Days: 7,
    mandatory: false,
    auto: true,
    requires_Manager: false,
    requires_Feedback: true,
    any_Lesson: false,
    lms: [],
    metric_Task: false,
    metric_Details: '{}',
    metric_Target: '{}',
});

export const ManageTask = ({
    values: initValues,
    show,
    defaultCompetency,
    reload,
    close,
    roles,
    enrollments,
    disableProfileEdit, // TODO - This stops the users being edited direct from a user objective page - Still required?
    testId,
    anonymous,
}) => {
    const dispatch = useDispatch();

    const [, updateState] = React.useState();
    const forceUpdate = React.useCallback(() => updateState({}), []);

    const [tab, setTab] = React.useState('assign');
    const [trackProfiles, setTrackProfiles] = React.useState(0);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [warnings, setWarnings] = React.useState({});
    const [saving, setSaving] = React.useState(false);
    const [metric, setMetric] = React.useState(null);

    const [warningsShown, setWarningsShown] = React.useState({});
    const { availableCompetencies } = useSelector(state => state.profile);
    const allCompetencies = useSelector(state => state.admin.competencies);
    const { downline, user } = useSelector(state => state.user);
    const { handles, tags } = useSelector(state => state.ui);
    const allMetrics = useSelector(state => state.dashboard.metrics);
    const [activeCompetencies, setActiveCompetencies] = React.useState([]);
    const [disabledUsers, setDisabledUsers] = React.useState([]);

    React.useEffect(() => {
        const message = SystemText('Objective/Manage/DisabledUser/Completed');
        const temp = values?.completed_Users?.map(x => {
            return {
                id: x,
                message,
            };
        });

        setDisabledUsers(temp);
    }, [values?.completed_Users]);

    React.useEffect(() => {
        const temp = initValues.pathway_Id ? allCompetencies : availableCompetencies;
        setActiveCompetencies(temp || []);
    }, [initValues.pathway_Id, allCompetencies, availableCompetencies]);

    React.useEffect(() => {
        if (show) {
            if (initValues.profile_Ids?.length > 0) {
                setTrackProfiles(initValues.profile_Ids.reduce((a, c) => a + c, 0));
                setTab('set');
            }

            const temp = { ...fallbackValues(), ...initValues };
            temp.owner_Id = temp.owner_Id || user?.id;
            if (defaultCompetency) temp.competency_Ids = [defaultCompetency];
            setValues(temp);

            api.loadAllCompetencies();
            api.loadAllMetrics();
        }
    }, [show]);

    React.useEffect(() => {
        if (tab === 'set') {
            api.loadCompetencies(values.profile_Ids);
        }
    }, [tab, trackProfiles]);

    React.useEffect(() => {
        const metric = getMetricFromPickerDetails(allMetrics, values.metric_Details, {});
        setMetric(metric);
    }, [values]);

    const api = {
        update: (entity, callback) =>
            dispatch(update('task', { ...entity, video: !!entity.video }, null, callback, true, 'objective')),
        add: (entity, callback) => dispatch(add('task', entity, null, callback, 'objective')),
        setShowSuccess: value => dispatch(setShowSuccess(value)),
        loadCompetencies: ids => dispatch(competenciesByProfiles(ids || [])),
        loadAllCompetencies: () => dispatch(load('competency', true)),
        loadAllMetrics: () =>
            apiGet('all-fields', user?.guid, response => dispatch(setDashboardCollection('metrics', response))),

        toggleFormHelp: () => dispatch(toggleFormHelp()),
    };

    const onChange = (value, ref, json) => {
        if (ref === 'metric_Details') {
            if (!value?.type) return;
            const metric = getMetricFromPickerDetails(allMetrics, JSON.stringify(value), {});
            setMetric(metric);
        }

        const temp = values;
        if (json) {
            temp[ref] = JSON.stringify(value);
        } else {
            temp[ref] = value;
        }

        setValues(temp);
        forceUpdate();

        if (ref === 'profile_Ids') {
            setTrackProfiles(value.reduce((a, c) => a + c, 0));
        }

        if (ref === 'profile_Ids' || ref === 'competency_Ids') {
            const temp = warningsShown;
            temp.profile_Competency_Ids = false;
            setWarningsShown(temp);
        }
    };

    const validate = task => {
        let result = true;
        const errorsTemp = {};
        const warningsTemp = {};
        const warningsShownTemp = warningsShown;

        if (!task.pathway_Id && task.profile_Ids.length === 0) {
            errorsTemp.profile_Ids = SystemText('Objective/Manage/Fields/ProfilePicker/Error/NotSet');
            result = false;
        }

        if (!task.name) {
            errorsTemp.name = SystemText('Objective/Manage/Fields/Name/Error');
            result = false;
        }

        if (task.lms_Task && !task.assets?.length) {
            errorsTemp.assets = SystemText('Objective/Manage/Fields/Resources/Errors/NoCourses');
            result = false;
        }

        if (task.metric_Task) {
            const taskDetails = JSON.parse(task.metric_Details);
            if (!taskDetails.metricRef && !taskDetails.metricId) {
                errorsTemp.metric_Details = SystemText('Objective/Manage/Fields/MetricPicker/Errors/NotSet');
                result = false;
            }

            const taskTarget = JSON.parse(task.metric_Target);
            if (!taskTarget.value) {
                errorsTemp.metric_Target = SystemText('Objective/Manage/Fields/MetricPicker/Errors/NotSet');
                result = false;
            } else if (isNaN(taskTarget.value)) {
                errorsTemp.metric_Target = SystemText('Objective/Manage/Fields/MetricPicker/Errors/Invalid');
                result = false;
            } else if (taskTarget.range === 'P' && !taskTarget.goal) {
                errorsTemp.metric_Target = SystemText('Objective/Manage/Fields/MetricPicker/Errors/NoGoal');
                result = false;
            }
        }

        if ((!task.competency_Ids || task.competency_Ids.length === 0) && !warningsShownTemp.competency_Ids) {
            warningsTemp.competency_Ids = SystemText('Objective/Manage/Fields/Competencies/Errors/NoneSet');
            warningsShownTemp.competency_Ids = true;
            result = false;
        }

        if (!warningsShownTemp.profile_Competency_Ids) {
            const maxWarningsShown = 5;
            const profilesWithNoCompetencies = task.profile_Ids
                ? task.profile_Ids.reduce((c, p, i) => {
                      const profile = downline.filter(d => d.id === p)[0];
                      const intersect = profile
                          ? profile.competency_Ids.filter(element => task.competency_Ids?.includes(element))
                          : [];
                      return !profile || profile.competency_Ids.length === 0 || intersect.length ? c : c.concat(profile);
                  }, [])
                : [];

            if (profilesWithNoCompetencies.length > 0) {
                warningsTemp.competency_Ids = () => {
                    return (
                        <span>
                            {SystemText('Objective/Manage/Fields/Competencies/Errors/NoMatching')}
                            <br />
                            {profilesWithNoCompetencies.map((p, i) => {
                                const profileComps = activeCompetencies.filter(c => p.competency_Ids.some(x => x === c.id));
                                if (i > maxWarningsShown) return null;
                                const extra = profilesWithNoCompetencies.length - maxWarningsShown;
                                if (i === maxWarningsShown)
                                    return (
                                        <div key={`comps-${p.id}`}>
                                            {SystemText('Objective/Manage/Fields/Competencies/Errors/NoMatching/Plus')}{' '}
                                            {extra} {StringHelpers.pluralise('other', extra)}
                                        </div>
                                    );
                                return (
                                    <span key={`comps-${p.id}`} className="mr10">
                                        - {p.full_Name}
                                        <Help id={`warning-${p.id}`}>
                                            <b>
                                                {SystemText(
                                                    'Objective/Manage/Fields/Competencies/Errors/NoMatching/UserTitle',
                                                    p.full_Name
                                                )}
                                            </b>
                                            <div>
                                                {profileComps.length === 0 ? (
                                                    <span>
                                                        {SystemText(
                                                            'Objective/Manage/Fields/Competencies/Errors/NoMatching/Message'
                                                        )}
                                                    </span>
                                                ) : (
                                                    profileComps.map(c => {
                                                        return <div key={`prof-comp-${p.id}-${c.id}`}>{c.full_Name}</div>;
                                                    })
                                                )}
                                            </div>
                                        </Help>
                                    </span>
                                );
                            })}
                        </span>
                    );
                };

                warningsShownTemp.profile_Competency_Ids = true;
                result = false;
            }
        }

        setErrors(errorsTemp);
        setWarnings(warningsTemp);
        setWarningsShown(warningsShownTemp);

        if (!result) document.getElementsByClassName('modal')[0].scrollTop = 0;

        return result;
    };

    const saveItem = () => {
        const task = { ...values };
        if (validate(task)) {
            if (task.assets?.some(a => a.source === 'accelerate')) {
                task.any_Lesson = true;
            }

            task.owner_Id = task.owner_Id || user.id;
            task.competency_Ids = task.competency_Ids || [];

            if (task.id) {
                api.update(task, closeModal);
            } else {
                api.add(task, () => {
                    const type = task.video ? 'Video' : task.metric_Task ? 'Metric' : task.lms_Task ? 'LMS' : 'Standard';
                    posthog.capture('objective-created', { type });
                    closeModal();
                });
            }
        } else {
            setSaving(false);
        }
    };

    const closeModal = blockReload => {
        if (blockReload === undefined) {
            blockReload = false;
        }

        setValues(fallbackValues());

        setErrors({});
        setWarnings({});
        setWarningsShown({});
        !blockReload && show && reload?.();
        close?.();
        setSaving(false);
        setTab('assign');
        dispatch(setPickerFilters([]));
    };

    const hasRole = user => {
        if (!roles || roles.length === 0) return true;
        return roles.some(r => user.role_Ids && user.role_Ids.indexOf(r) >= 0);
    };

    const competenciesMapped = activeCompetencies
        .map(c => translateCompetency(user.language_Code, c))
        .map(c => ({
            id: c.id,
            name: `${c.area} - ${c.name}`,
        }));

    const downlineMapped = downline
        ? downline
              .filter(d => d.active && d.id !== user.id && hasRole(d))
              .map(s => {
                  const p = enrollments ? enrollments.filter(p => p.profile_Id === s.id) : [];
                  return {
                      id: s.id,
                      name: s.full_Name,
                      disabled: p.length > 0,
                      extra:
                          p.length === 0
                              ? ''
                              : `${SystemText('Objective/Manage/Fields/ProfilePicker/Started')}: ${e4Format(p[0].enrolled)}`,
                  };
              })
        : [];

    const changeTab = () => {
        setTab(tab === 'assign' ? 'set' : 'assign');
    };

    const toggleHelp = () => {
        dispatch(toggleFormHelp());
    };

    let downlineMappedWithUser = downlineMapped;

    if (hasRole(user)) {
        const up = enrollments ? enrollments.filter(p => p.profile_Id === user.id) : [];
        const loggedonUser = [
            {
                id: user?.id,
                name: `${user?.full_Name} (${SystemText('Objective/Manage/Fields/ProfilePicker/You')})`,
                disabled: up.length > 0,
                extra:
                    up.length === 0
                        ? ''
                        : `${SystemText('Objective/Manage/Fields/ProfilePicker/Started')}: ${e4Format(up[0].enrolled)}`,
            },
        ];
        downlineMappedWithUser = loggedonUser.concat(downlineMapped);
    }

    const footer = React.useMemo(
        () => (
            <div>
                {tab === 'assign' && !anonymous ? (
                    <button
                        data-testid="assign-button"
                        className="btn btn-primary"
                        onClick={changeTab}
                        disabled={!trackProfiles}
                    >
                        {SystemText('General/Buttons/Next')}
                    </button>
                ) : (
                    <SaveButton
                        data-testid="save-button"
                        saving={saving}
                        setSaving={setSaving}
                        className="btn btn-success"
                        disabled={!trackProfiles && !anonymous}
                        onClick={saveItem}
                    >
                        {SystemText('General/Buttons/Save')}
                    </SaveButton>
                )}
                <button data-testid="cancel-button" className="btn" onClick={() => closeModal(true)}>
                    {SystemText('General/Buttons/Cancel')}
                </button>
            </div>
        ),
        [tab, trackProfiles, values]
    );

    const defaultProps = (ref, json) => {
        const result = {
            id: ref,
            value: json ? JSON.parse(values[ref] || '{}') : values[ref],
            error: errors[ref],
            onChange: e => onChange(e, ref, json),
            onSubmit: () => saveItem(),
        };

        if (ref === 'metric_Target') {
            result.value = {
                value: result.value?.value,
                range: result.value?.range,
                goal: result.value?.goal,
                period: result.value?.period,
                duration: result.value?.duration,
            };
        }

        return result;
    };

    const nameField = (
        <Forms.Text
            {...defaultProps('name')}
            label={SystemText('Objective/Manage/Fields/Name/Label')}
            help={SystemText('Objective/Manage/Fields/Name/Help')}
            placeholder={SystemText('Objective/Manage/Fields/Name/Placeholder')}
            maxLength="100"
            mandatory
        />
    );

    const ownerField =
        user?.administrator && !initValues.pathway_Id ? (
            <Forms.Select
                {...defaultProps('owner_Id')}
                label={SystemText('Objective/Manage/Fields/Owner/Label')}
                help={SystemText('Objective/Manage/Fields/Owner/Help')}
                data={downlineMappedWithUser}
                mandatory
            />
        ) : null;

    const competencyIdField = (
        <Forms.Select
            {...defaultProps('competency_Ids')}
            allowMultiple
            label={SystemText('Objective/Manage/Fields/Competencies/Label')}
            data={competenciesMapped}
            warning={warnings.competency_Ids}
            help={SystemText('Objective/Manage/Fields/Competencies/Help')}
            placeholder={
                !initValues.pathway_Id && Array.isArray(values.profile_Ids) && values.profile_Ids.length > 0
                    ? SystemText('Objective/Manage/Fields/Competencies/Placeholder')
                    : ''
            }
        />
    );

    const descriptionField = (
        <Forms.Textarea
            tags={tags}
            handles={handles}
            {...defaultProps('description')}
            label={SystemText('Objective/Manage/Fields/Summary/Label')}
            help={SystemText('Objective/Manage/Fields/Summary/Help')}
            rows="2"
        />
    );

    const dueField = !initValues.pathway_Id ? (
        <Forms.Date
            {...defaultProps('due')}
            extraProps={{ id: 'date-due' }}
            label={SystemText('Objective/Manage/Fields/Due/Label')}
            help={SystemText('Objective/Manage/Fields/Due/Help')}
            allowReoccurring
        />
    ) : null;

    const instructionsField = (
        <Forms.Textarea
            tags={tags}
            handles={handles}
            {...defaultProps('instructions')}
            help={SystemText('Objective/Manage/Fields/Instructions/Help')}
            placeholder={SystemText('Objective/Manage/Fields/Instructions/Placeholder')}
            label={SystemText('Objective/Manage/Fields/Instructions/Label')}
            rows="6"
        />
    );

    const assetsField = lms => {
        const stub = lms ? 'Lms' : 'Resources';
        const allowed = lms ? lmsSystems : null;
        const buttonLabel = lms ? SystemText('Objective/Manage/Fields/Lms/Button') : '';

        return (
            <div>
                <Files
                    {...defaultProps('assets')}
                    label={SystemText(`Objective/Manage/Fields/${stub}/Label`)}
                    folder={uploadType.taskFile}
                    allowMultiple
                    allowedSources={allowed}
                    anonymous={anonymous}
                    mandatory
                    modalInModal
                    profileIds={values.profile_Ids}
                    buttonLabel={buttonLabel}
                    help={SystemText(`Objective/Manage/Fields/${stub}/Help`)}
                />
                {lms &&
                    ArrayHelpers.existsAndHasLength(values.assets) &&
                    values.assets.some(a => lmsSystems.some(x => x === a.source)) && (
                        <div className="mb5">
                            {(values.lms_Task || ArrayHelpers.existsAndHasLength(values.lms)) && (
                                <div className="flex flex-start">
                                    <input
                                        type="checkbox"
                                        id="any-learning"
                                        disabled={values.assets.some(a => a.source === 'accelerate')}
                                        checked={!values.any_Lesson && !values.assets.some(a => a.source === 'accelerate')}
                                        onChange={e => setValues({ ...values, any_Lesson: !e.target.checked })}
                                    />
                                    <label className="ml10" htmlFor="any-learning" style={{ fontWeight: 400 }}>
                                        {SystemText('Objective/Manage/Fields/Resources/AnyLesson')}
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
            </div>
        );
    };

    const taskTypeField = (
        <Forms.Buttons
            {...defaultProps('taskType')}
            label={SystemText('Objective/Manage/Fields/Type/Label')}
            help={
                values.video
                    ? SystemText('Objective/Manage/Fields/Type/Help/Video')
                    : values.metric_Task
                    ? SystemText('Objective/Manage/Fields/Type/Help/MetricBased')
                    : values.lms_Task
                    ? SystemText('Objective/Manage/Fields/Type/Help/Learn')
                    : SystemText('Objective/Manage/Fields/Type/Help/Standard')
            }
        >
            <button
                className={`btn ${values.video || values.metric_Task || values.lms_Task ? '' : 'btn-primary'}`}
                onClick={() =>
                    setValues({ ...values, video: false, metric_Task: false, lms_Task: false, requires_Manager: false })
                }
            >
                <i className="fa fa-calendar-check mr5" />
                {SystemText('Objective/Manage/Fields/Type/Buttons/Standard')}
            </button>
            <button
                className={`btn ${values.video ? 'btn-primary' : ''}`}
                onClick={() =>
                    setValues({ ...values, video: true, metric_Task: false, lms_Task: false, requires_Manager: false })
                }
            >
                <i className="fa fa-video-camera mr5" />
                {SystemText('Objective/Manage/Fields/Type/Buttons/Video')}
            </button>
            {hasModule(user, 'integrations') && (
                <>
                    <button
                        className={`btn ${values.metric_Task ? 'btn-primary' : ''}`}
                        onClick={() =>
                            setValues({
                                ...values,
                                video: false,
                                metric_Task: true,
                                lms_Task: false,
                                requires_Manager: true,
                            })
                        }
                    >
                        <i className="fa fa-bullseye mr5" />
                        {SystemText('Objective/Manage/Fields/Type/Buttons/MetricBased')}
                    </button>
                    {user.integrations.some(i => lmsSystemRefs.some(r => r === i.ref)) && (
                        <button
                            className={`btn ${values.lms_Task ? 'btn-primary' : ''}`}
                            onClick={() =>
                                setValues({
                                    ...values,
                                    video: false,
                                    metric_Task: false,
                                    lms_Task: true,
                                    requires_Manager: false,
                                })
                            }
                        >
                            <i className="fa fa-graduation-cap mr5" />
                            {SystemText('Objective/Manage/Fields/Type/Buttons/Learn')}
                        </button>
                    )}
                </>
            )}
        </Forms.Buttons>
    );

    const metricsFields = hasModule(user, 'integrations') ? (
        <div>
            <div className="row" style={values.metric_Task ? null : { display: 'none' }}>
                <div className="col-md-12">
                    <MetricPicker
                        label={SystemText('Objective/Manage/Fields/MetricPicker/Label')}
                        {...defaultProps('metric_Details', true)}
                        mandatory
                        hideRatioMetrics
                        hideSubMetrics
                    />
                </div>
                <div className="col-md-12">
                    <MetricTarget
                        {...defaultProps('metric_Target', true)}
                        mandatory
                        metric={metric}
                        assigned={values.date_Assigned || new Date()}
                        due={values.due}
                    />
                </div>
            </div>
        </div>
    ) : null;

    const taskForm = (
        <div className="form-group mt10" id="manage-task-form" key={`manage-task-${values.id}`} data-testid={testId}>
            {nameField}
            {competencyIdField}
            {descriptionField}
            {values.pathway_Id ? (
                <div className="row">
                    <div className="col-md-12">{taskTypeField}</div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-md-12">{dueField}</div>
                    <div className="col-md-12">{taskTypeField}</div>
                </div>
            )}
            {metricsFields}
            {values.lms_Task && <>{assetsField(true)}</>}
            <div className="row">
                <div className="col-md-6">
                    <Forms.Toggle
                        {...defaultProps('requires_Manager')}
                        disabled={values.metric_Task}
                        label={SystemText('Objective/Manage/Fields/ManagerApproval/Label')}
                        help={SystemText('Objective/Manage/Fields/ManagerApproval/Help')}
                    />
                </div>
                <div className="col-md-6">
                    {values.requires_Manager && (
                        <Forms.Toggle
                            {...defaultProps('requires_Feedback')}
                            label={SystemText('Objective/Manage/Fields/RequiresFeedback/Label')}
                            help={SystemText('Objective/Manage/Fields/RequiresFeedback/Help')}
                        />
                    )}
                </div>
                {values.lms_Task && (
                    <div className="col-sm-6">
                        <Forms.Toggle
                            {...defaultProps('auto')}
                            label={SystemText('Objective/Manage/Fields/Auto/Label')}
                            help={SystemText('Objective/Manage/Fields/Auto/Help')}
                        />
                    </div>
                )}
            </div>

            {!values.lms_Task ? (
                <TitleBar
                    title={SystemText('Objective/Manage/Fields/MoreOptions/Closed')}
                    openTitle={SystemText('Objective/Manage/Fields/MoreOptions/Open')}
                    className="showMore"
                    data-testid="span-show-more"
                >
                    {assetsField(false)}
                    {instructionsField}
                    {ownerField}
                </TitleBar>
            ) : (
                instructionsField
            )}
        </div>
    );

    return (
        <Modal
            toggleFormHelp={toggleHelp}
            close={() => closeModal(true)}
            footer={footer}
            show={show}
            tab={anonymous ? null : tab}
            setTab={anonymous ? null : setTab}
            title={anonymous ? SystemText('Objective/Manage/Tabs/Set') : null}
            className={`modal-body-full ${anonymous ? '' : 'modal-920'}`}
            tabs={anonymous ? null : ['assign', 'set']}
            tabIcons={
                anonymous
                    ? null
                    : tab === 'assign'
                    ? ['fa-circle-1 fa-solid fa-tab', 'fa-circle-2 fa-tab']
                    : ['fa-circle-check fa-tab', 'fa-circle-2 fa-solid fa-tab']
            }
            iconsPre
            tabNames={
                anonymous
                    ? null
                    : { assign: SystemText('Objective/Manage/Tabs/Assign'), set: SystemText('Objective/Manage/Tabs/Set') }
            }
        >
            {tab === 'assign' && !anonymous ? (
                <ModalPicker
                    value={values?.profile_Ids}
                    disabledUsers={disabledUsers}
                    onChange={ids => onChange(ids, 'profile_Ids')}
                />
            ) : (
                <div>
                    {values?.profile_Ids?.length || anonymous ? (
                        taskForm
                    ) : (
                        <div className="mb20 mt20 flex flex-center">
                            <i className="fa fa-warning fa-lg mr10 text-danger" />
                            <span className="flex-grow">
                                <Copy value={SystemText('Objective/Manage/NoUsers')} />
                            </span>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default ManageTask;
